import { useState, useEffect } from "react";
import axios from "axios";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  DollarSign, 
  Loader2,
  TrendingUp,
  CheckCircle,
  Clock,
  Percent
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DistributorEarnings({ distributor }) {
  const [earnings, setEarnings] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("session_token")}` }
  });

  useEffect(() => {
    if (distributor) {
      fetchEarnings();
    }
  }, [distributor]);

  const fetchEarnings = async () => {
    try {
      setLoading(true);
      const [earningsRes, statsRes] = await Promise.all([
        axios.get(`${API}/distributor/earnings`, getAuthHeaders()),
        axios.get(`${API}/distributor/stats`, getAuthHeaders())
      ]);
      setEarnings(earningsRes.data || []);
      setSummary(statsRes.data);
    } catch (error) {
      console.error("Failed to fetch earnings:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="distributor-earnings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Earnings
        </h1>
        <p className="text-slate-500">Your commission earnings breakdown</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <Percent className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-sm text-slate-500">Commission Rate</span>
          </div>
          <p className="text-2xl font-bold text-slate-900">
            {distributor?.commission_percent || 10}%
          </p>
        </div>
        
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <span className="text-sm text-slate-500">Total Earned</span>
          </div>
          <p className="text-2xl font-bold text-green-600">
            ${(summary?.total_earnings || 0).toFixed(2)}
          </p>
        </div>
        
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-amber-600" />
            </div>
            <span className="text-sm text-slate-500">Pending</span>
          </div>
          <p className="text-2xl font-bold text-amber-600">
            ${(summary?.pending_earnings || 0).toFixed(2)}
          </p>
        </div>
        
        <div className="bg-white rounded-xl border p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-blue-600" />
            </div>
            <span className="text-sm text-slate-500">Paid Out</span>
          </div>
          <p className="text-2xl font-bold text-blue-600">
            ${(summary?.paid_earnings || 0).toFixed(2)}
          </p>
        </div>
      </div>

      {/* Earnings Table */}
      <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-slate-900">Commission History</h2>
          <p className="text-sm text-slate-500">Detailed breakdown of your earnings</p>
        </div>
        
        {earnings.length === 0 ? (
          <div className="text-center py-12">
            <DollarSign className="w-12 h-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">No earnings yet</p>
            <p className="text-sm text-slate-400">Commissions will appear here when your resellers make sales</p>
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Order</TableHead>
                <TableHead>Reseller</TableHead>
                <TableHead className="text-right">Order Subtotal</TableHead>
                <TableHead className="text-right">Commission</TableHead>
                <TableHead className="text-center">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {earnings.map((earning, idx) => (
                <TableRow key={idx}>
                  <TableCell className="text-sm text-slate-500">
                    {new Date(earning.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    #{earning.order_id?.slice(-8)}
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{earning.store_name}</p>
                  </TableCell>
                  <TableCell className="text-right">
                    ${(earning.order_subtotal || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right font-medium text-green-600">
                    +${(earning.commission || 0).toFixed(2)}
                  </TableCell>
                  <TableCell className="text-center">
                    <Badge variant={earning.paid ? "default" : "secondary"}>
                      {earning.paid ? "Paid" : "Pending"}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
